import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  Settings, 
  Save, 
  Shield, 
  Clock, 
  Bell, 
  Calendar,
  RefreshCw,
  Play,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Lock,
  Wallet,
  Percent,
  Building2,
  Users,
  Mail,
  Activity,
  Trash2,
  Monitor,
  Eye,
  CreditCard
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function ChangePasswordForm({ token }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const handleSubmit = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Le nouveau mot de passe doit faire au moins 6 caractères');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Mot de passe modifié avec succès');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 max-w-md">
      <div className="space-y-2">
        <Label>Mot de passe actuel</Label>
        <div className="relative">
          <Input
            type={showCurrent ? 'text' : 'password'}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="Votre mot de passe actuel"
            data-testid="current-password"
          />
          <button type="button" onClick={() => setShowCurrent(!showCurrent)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground">
            {showCurrent ? 'Masquer' : 'Afficher'}
          </button>
        </div>
      </div>
      <div className="space-y-2">
        <Label>Nouveau mot de passe</Label>
        <div className="relative">
          <Input
            type={showNew ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Nouveau mot de passe (min 6 caractères)"
            data-testid="new-password"
          />
          <button type="button" onClick={() => setShowNew(!showNew)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground">
            {showNew ? 'Masquer' : 'Afficher'}
          </button>
        </div>
      </div>
      <div className="space-y-2">
        <Label>Confirmer le nouveau mot de passe</Label>
        <Input
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Retapez le nouveau mot de passe"
          data-testid="confirm-password"
        />
        {confirmPassword && newPassword !== confirmPassword && (
          <p className="text-xs text-red-500">Les mots de passe ne correspondent pas</p>
        )}
      </div>
      <Button onClick={handleSubmit} disabled={saving} className="mt-2" data-testid="change-password-btn">
        <Lock className="w-4 h-4 mr-2" />
        {saving ? 'Modification...' : 'Modifier le mot de passe'}
      </Button>
    </div>
  );
}

function ChangeEmailForm({ token, user }) {
  const [newEmail, setNewEmail] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!newEmail || newEmail === user?.email) {
      toast.error('Veuillez entrer un nouvel email');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/api/auth/update-email`, { new_email: newEmail }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update localStorage directly
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      stored.email = newEmail;
      localStorage.setItem('user', JSON.stringify(stored));
      toast.success('Email modifié avec succès. Rechargement...');
      setNewEmail('');
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 max-w-md">
      <div className="space-y-2">
        <Label>Nouvel email</Label>
        <Input
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder="nouveau@email.com"
          data-testid="change-email-input"
        />
      </div>
      <Button onClick={handleSubmit} disabled={saving || !newEmail} data-testid="change-email-btn">
        <Mail className="w-4 h-4 mr-2" />
        {saving ? 'Modification...' : 'Modifier l\'email'}
      </Button>
    </div>
  );
}

function ResetDatabaseButton({ token }) {
  const [confirmation, setConfirmation] = useState('');
  const [resetting, setResetting] = useState(false);

  const handleReset = async () => {
    if (confirmation !== 'RESET') {
      toast.error('Tapez RESET en majuscules pour confirmer');
      return;
    }
    setResetting(true);
    try {
      const res = await axios.post(`${API}/api/backup/reset-database`, { confirmation: 'RESET' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      setConfirmation('');
      // Clear all auth data and redirect to wizard
      localStorage.clear();
      sessionStorage.clear();
      setTimeout(() => { window.location.href = '/'; }, 1000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="space-y-3 max-w-md">
      <p className="text-sm text-red-400">Pour confirmer, tapez <strong>RESET</strong> dans le champ ci-dessous :</p>
      <Input
        value={confirmation}
        onChange={(e) => setConfirmation(e.target.value)}
        placeholder="Tapez RESET pour confirmer"
        className="border-red-500/30"
        data-testid="reset-confirmation"
      />
      <Button
        onClick={handleReset}
        disabled={confirmation !== 'RESET' || resetting}
        variant="destructive"
        data-testid="reset-database-btn"
      >
        <AlertTriangle className="w-4 h-4 mr-2" />
        {resetting ? 'Réinitialisation...' : 'Réinitialiser la base de données'}
      </Button>
    </div>
  );
}

function ErrorMonitoringPanel({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [filter, setFilter] = useState('all');

  const fetchErrors = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (filter !== 'all') params.append('browser', filter);
      const res = await axios.get(`${API}/api/errors/reports?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch {
      toast.error('Erreur lors du chargement du monitoring');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchErrors(); }, [filter]);

  const handleClear = async () => {
    if (!window.confirm('Supprimer tous les rapports d\'erreurs ?')) return;
    setClearing(true);
    try {
      await axios.delete(`${API}/api/errors/reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rapports supprimés');
      fetchErrors();
    } catch {
      toast.error('Erreur lors de la suppression');
    } finally {
      setClearing(false);
    }
  };

  const stats = data?.stats || {};
  const reports = data?.reports || [];

  const browserColors = {
    Chrome: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    Edge: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    Firefox: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    Safari: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    Unknown: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  };

  const typeColors = {
    boundary: 'bg-red-500/20 text-red-400',
    runtime: 'bg-orange-500/20 text-orange-400',
    unhandled: 'bg-yellow-500/20 text-yellow-400',
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total erreurs</p>
                <p className="text-2xl font-bold">{stats.total || 0}</p>
              </div>
              <Monitor className="w-8 h-8 text-muted-foreground/30" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Dernières 24h</p>
                <p className="text-2xl font-bold">{stats.recent_24h || 0}</p>
              </div>
              <Activity className={`w-8 h-8 ${(stats.recent_24h || 0) > 0 ? 'text-red-400' : 'text-green-400'}`} />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Par navigateur</p>
                <div className="flex gap-2 mt-1 flex-wrap">
                  {(stats.by_browser || []).map(b => (
                    <Badge key={b.browser} variant="outline" className={browserColors[b.browser] || browserColors.Unknown}>
                      {b.browser}: {b.count}
                    </Badge>
                  ))}
                  {(!stats.by_browser || stats.by_browser.length === 0) && (
                    <span className="text-sm text-muted-foreground">Aucune donnée</span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Errors */}
      {stats.top_errors && stats.top_errors.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-orange-400" />
              Erreurs les plus fréquentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.top_errors.map((err, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30">
                  <Badge variant="outline" className="bg-red-500/10 text-red-400 border-red-500/20 shrink-0">
                    x{err.count}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-mono truncate">{err.message}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-muted-foreground">Dernière: {formatDate(err.last_seen)}</span>
                      <span className="text-xs text-muted-foreground">Navigateurs: {(err.browsers || []).join(', ')}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters + Actions */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Filtre:</span>
          {['all', 'Chrome', 'Edge', 'Firefox', 'Safari'].map(b => (
            <Button
              key={b}
              size="sm"
              variant={filter === b ? 'default' : 'outline'}
              onClick={() => setFilter(b)}
              data-testid={`filter-${b.toLowerCase()}`}
            >
              {b === 'all' ? 'Tous' : b}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={fetchErrors} data-testid="refresh-errors-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </Button>
          <Button size="sm" variant="destructive" onClick={handleClear} disabled={clearing} data-testid="clear-errors-btn">
            <Trash2 className="w-4 h-4 mr-1" /> {clearing ? 'Suppression...' : 'Vider'}
          </Button>
        </div>
      </div>

      {/* Error List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Journal des erreurs ({reports.length})</CardTitle>
          <CardDescription>Erreurs JavaScript remontées par les navigateurs clients</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-green-400" />
              <p className="font-medium">Aucune erreur enregistrée</p>
              <p className="text-sm mt-1">Tout fonctionne correctement</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {reports.map((r, i) => (
                <div key={i} className="p-3 rounded-lg border bg-card/50 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-mono text-red-300 flex-1 min-w-0 truncate" title={r.message}>{r.message}</p>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Badge variant="outline" className={browserColors[r.browser] || browserColors.Unknown}>
                        {r.browser || '?'}
                      </Badge>
                      <Badge variant="outline" className={typeColors[r.error_type] || typeColors.runtime}>
                        {r.error_type || 'runtime'}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span>{formatDate(r.created_at)}</span>
                    {r.user_name && <span>Utilisateur: {r.user_name}</span>}
                    {r.user_role && <Badge variant="outline" className="text-[10px] h-4">{r.user_role}</Badge>}
                    {r.page_url && <span className="truncate max-w-[200px]" title={r.page_url}>{r.page_url.replace(/^https?:\/\/[^/]+/, '')}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdminSettings() {
  const token = localStorage.getItem('token');
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('platform');
  
  // Platform Settings
  const [platformSettings, setPlatformSettings] = useState({
    platform_name: '',
    gate_pin: '',
    gate_pin_enabled: true,
    default_language: 'fr'
  });
  
  // Client Template Settings
  const [templateSettings, setTemplateSettings] = useState({
    pin_enabled: true,
    pin_length: 6,
    pin_max_attempts: 3,
    login_max_attempts: 0,
    session_timeout_minutes: 30,
    session_warning_minutes: 5,
    default_currency: 'EUR',
    default_portfolio_balance: 0
  });
  
  // Scheduler Status
  const [schedulerStatus, setSchedulerStatus] = useState({
    scheduler_running: false,
    jobs: [],
    recent_logs: []
  });
  
  // Notification Settings
  const [notificationSettings, setNotificationSettings] = useState({
    transaction_notifications: true,
    interest_notifications: true,
    contract_expiry_notifications: true,
    kyc_notifications: true
  });
  const [adminSignature, setAdminSignature] = useState(null);
  const [adminSignatureLabel, setAdminSignatureLabel] = useState('');
  const [backups, setBackups] = useState([]);
  const [backupInterval, setBackupInterval] = useState(30);
  const [backupLoading, setBackupLoading] = useState(false);
  const [showBackupLogs, setShowBackupLogs] = useState(null);
  const [showStorageGuide, setShowStorageGuide] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchSchedulerStatus();
  }, []);

  const fetchSettings = async () => {
    try {
      const [platformRes, templateRes, sigRes] = await Promise.all([
        axios.get(`${API}/api/settings/platform`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/settings/template`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/settings/admin-signature`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { admin_signature: null } }))
      ]);
      
      setPlatformSettings(prev => ({ ...prev, ...platformRes.data }));
      setTemplateSettings(prev => ({ ...prev, ...templateRes.data }));
      setAdminSignature(sigRes.data?.admin_signature || null);
      setAdminSignatureLabel(platformRes.data?.admin_signature_label || '');
      setBackupInterval(platformRes.data?.backup_interval_minutes || 30);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedulerStatus = async () => {
    try {
      const response = await axios.get(`${API}/api/scheduler/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchedulerStatus(response.data);
    } catch (error) {
      console.error('Error fetching scheduler status:', error);
    }
  };

  const fetchBackups = async () => {
    try {
      const res = await axios.get(`${API}/api/backup/list`, { headers: { Authorization: `Bearer ${token}` } });
      setBackups(res.data.backups || []);
      setBackupInterval(res.data.backup_interval_minutes || 30);
    } catch (e) { console.debug('Backups fetch:', e); }
  };



  const savePlatformSettings = async () => {
    setSaving(true);
    try {
      // Only send relevant fields, exclude heavy base64 data
      const { admin_signature, logo_url, custom_colors, template_id, ...rest } = platformSettings;
      const settingsToSave = {
        platform_name: rest.platform_name,
        default_language: rest.default_language,
        gate_pin: rest.gate_pin,
        gate_pin_enabled: rest.gate_pin_enabled,
        platform_address: rest.platform_address,
        platform_website: rest.platform_website,
        platform_email: rest.platform_email,
        platform_timezone: rest.platform_timezone,
        admin_signature_label: rest.admin_signature_label,
        design_version: rest.design_version,
      };
      await axios.put(`${API}/api/settings/platform`, settingsToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Paramètres de la plateforme enregistrés');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const saveTemplateSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/settings/template`, templateSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Paramètres client enregistrés');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const triggerJob = async (jobId) => {
    try {
      await axios.post(`${API}/api/scheduler/trigger/${jobId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tâche déclenchée avec succès');
      fetchSchedulerStatus();
    } catch (error) {
      toast.error('Erreur lors du déclenchement');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Settings className="w-6 h-6" />
              Paramètres de la Plateforme
            </h2>
            <p className="text-muted-foreground text-sm mt-1">
              Configuration générale, sécurité et automatisations
            </p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="inline-flex w-auto overflow-x-auto">
            <TabsTrigger value="platform" className="flex items-center gap-2" data-testid="tab-platform">
              <Building2 className="w-4 h-4" />
              Plateforme
            </TabsTrigger>
            <TabsTrigger value="clients" className="flex items-center gap-2" data-testid="tab-clients">
              <Users className="w-4 h-4" />
              Clients
            </TabsTrigger>
            <TabsTrigger value="scheduler" className="flex items-center gap-2" data-testid="tab-scheduler">
              <Clock className="w-4 h-4" />
              Scheduler
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2" data-testid="tab-notifications">
              <Bell className="w-4 h-4" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="backup" className="flex items-center gap-2" data-testid="tab-backup">
              <Shield className="w-4 h-4" />
              Sauvegarde
            </TabsTrigger>
            <TabsTrigger value="account" className="flex items-center gap-2" data-testid="tab-account">
              <Lock className="w-4 h-4" />
              Mon compte
            </TabsTrigger>
            <TabsTrigger value="monitoring" className="flex items-center gap-2" data-testid="tab-monitoring">
              <Activity className="w-4 h-4" />
              Monitoring
            </TabsTrigger>
          </TabsList>

          {/* Platform Settings Tab */}
          <TabsContent value="platform" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Informations Générales
                </CardTitle>
                <CardDescription>
                  Configuration de base de votre plateforme bancaire
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="platform_name">Nom de la plateforme</Label>
                    <Input
                      id="platform_name"
                      value={platformSettings.platform_name || ''}
                      onChange={(e) => setPlatformSettings(prev => ({ ...prev, platform_name: e.target.value }))}
                      placeholder="Ma Banque Privée"
                      data-testid="platform-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="default_language">Langue par défaut</Label>
                    <select
                      id="default_language"
                      className="w-full h-10 px-3 rounded-md border border-input bg-background"
                      value={platformSettings.default_language || 'fr'}
                      onChange={(e) => setPlatformSettings(prev => ({ ...prev, default_language: e.target.value }))}
                    >
                      <option value="fr">Français</option>
                      <option value="en">English</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Adresse / Siège social (apparaît dans les contrats et pied de page PDF)</Label>
                  <Input
                    value={platformSettings.platform_address || ''}
                    onChange={(e) => setPlatformSettings(prev => ({ ...prev, platform_address: e.target.value }))}
                    placeholder="Triq Mikiel Ang Borg, St Julians SPK 1000 Malta - C88392"
                    data-testid="platform-address-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Site web</Label>
                    <Input
                      value={platformSettings.platform_website || ''}
                      onChange={(e) => setPlatformSettings(prev => ({ ...prev, platform_website: e.target.value }))}
                      placeholder="www.example.com"
                      data-testid="platform-website-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email de contact</Label>
                    <Input
                      value={platformSettings.platform_email || ''}
                      onChange={(e) => setPlatformSettings(prev => ({ ...prev, platform_email: e.target.value }))}
                      placeholder="contact@example.com"
                      data-testid="platform-email-input"
                    />
                  </div>
                </div>

                {/* Timezone */}
                <div className="space-y-2">
                  <Label>Fuseau horaire de la plateforme</Label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background"
                    value={platformSettings.platform_timezone || 'Europe/Paris'}
                    onChange={(e) => setPlatformSettings(prev => ({ ...prev, platform_timezone: e.target.value }))}
                    data-testid="platform-timezone-select"
                  >
                    <option value="Europe/Paris">Europe/Paris (UTC+1/+2)</option>
                    <option value="Europe/London">Europe/London (UTC+0/+1)</option>
                    <option value="Europe/Zurich">Europe/Zurich (UTC+1/+2)</option>
                    <option value="Europe/Brussels">Europe/Brussels (UTC+1/+2)</option>
                    <option value="Europe/Luxembourg">Europe/Luxembourg (UTC+1/+2)</option>
                    <option value="Europe/Berlin">Europe/Berlin (UTC+1/+2)</option>
                    <option value="Europe/Rome">Europe/Rome (UTC+1/+2)</option>
                    <option value="Europe/Madrid">Europe/Madrid (UTC+1/+2)</option>
                    <option value="Europe/Lisbon">Europe/Lisbon (UTC+0/+1)</option>
                    <option value="Europe/Amsterdam">Europe/Amsterdam (UTC+1/+2)</option>
                    <option value="Europe/Vienna">Europe/Vienna (UTC+1/+2)</option>
                    <option value="Europe/Malta">Europe/Malta (UTC+1/+2)</option>
                    <option value="Europe/Moscow">Europe/Moscow (UTC+3)</option>
                    <option value="Europe/Istanbul">Europe/Istanbul (UTC+3)</option>
                    <option value="America/New_York">America/New York (UTC-5/-4)</option>
                    <option value="America/Chicago">America/Chicago (UTC-6/-5)</option>
                    <option value="America/Los_Angeles">America/Los Angeles (UTC-8/-7)</option>
                    <option value="America/Montreal">America/Montreal (UTC-5/-4)</option>
                    <option value="Asia/Dubai">Asia/Dubai (UTC+4)</option>
                    <option value="Asia/Singapore">Asia/Singapore (UTC+8)</option>
                    <option value="Asia/Hong_Kong">Asia/Hong Kong (UTC+8)</option>
                    <option value="Asia/Tokyo">Asia/Tokyo (UTC+9)</option>
                    <option value="Africa/Casablanca">Africa/Casablanca (UTC+0/+1)</option>
                    <option value="Africa/Tunis">Africa/Tunis (UTC+1)</option>
                    <option value="Pacific/Noumea">Pacific/Nouméa (UTC+11)</option>
                    <option value="Indian/Reunion">Indian/Réunion (UTC+4)</option>
                  </select>
                  <p className="text-xs text-muted-foreground">Ce fuseau horaire s'applique à toute la plateforme (dates des contrats, transactions, etc.)</p>
                </div>

                {/* Gate PIN */}
                <div className="p-4 rounded-lg border bg-muted/30">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Lock className="w-5 h-5 text-amber-500" />
                      <div>
                        <Label className="text-base">PIN d&apos;accès à la plateforme</Label>
                        <p className="text-sm text-muted-foreground">
                          Code PIN requis pour accéder à la page de connexion
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={platformSettings.gate_pin_enabled}
                      onCheckedChange={(checked) => setPlatformSettings(prev => ({ ...prev, gate_pin_enabled: checked }))}
                      data-testid="gate-pin-switch"
                    />
                  </div>
                  {platformSettings.gate_pin_enabled && (
                    <div className="space-y-2">
                      <Label htmlFor="gate_pin">Code PIN (6 chiffres)</Label>
                      <Input
                        id="gate_pin"
                        type="password"
                        value={platformSettings.gate_pin || ''}
                        onChange={(e) => setPlatformSettings(prev => ({ ...prev, gate_pin: e.target.value }))}
                        placeholder="******"
                        maxLength={6}
                        data-testid="gate-pin-input"
                      />
                    </div>
                  )}
                </div>

                {/* Design Version Toggle */}
                <div className="p-4 rounded-lg border bg-muted/30">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Eye className="w-5 h-5 text-purple-500" />
                      <div>
                        <Label className="text-base">Design de l'espace client</Label>
                        <p className="text-sm text-muted-foreground">
                          Choisir le style d'interface pour vos clients
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                      { key: 'v1', label: 'V1 — Classique', desc: 'Sidebar verticale traditionnelle', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
                      { key: 'v2', label: 'V2 — Premium Banking', desc: 'Design ultra-premium avec sidebar dorée', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
                      { key: 'v3', label: 'V3 — Dashboard Financier', desc: 'Navigation horizontale moderne', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
                      { key: 'v4', label: 'V4 — App Trading', desc: 'Navigation icônes + texte, glassmorphism', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
                    ].map(opt => (
                      <button
                        key={opt.key}
                        onClick={() => setPlatformSettings(prev => ({ ...prev, design_version: opt.key }))}
                        className={`p-4 rounded-xl border-2 text-left transition-all duration-200 ${
                          platformSettings.design_version === opt.key
                            ? 'border-primary ring-2 ring-primary/20'
                            : 'border-border/50 hover:border-border'
                        }`}
                        data-testid={`design-version-${opt.key}`}
                      >
                        <Badge variant="outline" className={platformSettings.design_version === opt.key ? opt.color : 'bg-muted/50'}>
                          {opt.label}
                        </Badge>
                        <p className="text-xs text-muted-foreground mt-2">{opt.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* IBAN Mode */}
                <div className="p-4 rounded-lg border bg-muted/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CreditCard className="w-5 h-5 text-blue-500" />
                      <div>
                        <Label className="text-base">Mode de gestion des RIB</Label>
                        <p className="text-sm text-muted-foreground">
                          {platformSettings.iban_mode === 'pool'
                            ? "Pool centralisé : l'admin crée les RIB et les attribue aux analystes"
                            : "Mode analyste : chaque analyste crée les RIB pour ses clients"}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={platformSettings.iban_mode === 'pool'}
                      onCheckedChange={(checked) => setPlatformSettings(prev => ({ ...prev, iban_mode: checked ? 'pool' : 'analyst' }))}
                      data-testid="iban-mode-switch"
                    />
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="outline" className={platformSettings.iban_mode === 'pool' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 'bg-muted/50'}>
                      {platformSettings.iban_mode === 'pool' ? 'Pool centralisé' : 'Mode analyste (par défaut)'}
                    </Badge>
                  </div>
                </div>

                {/* Admin Signature for Contracts */}
                <div className="space-y-3 pt-4 border-t border-border/50">
                  <Label className="text-sm font-semibold">Signature pour les contrats</Label>
                  <p className="text-xs text-muted-foreground">
                    Cette signature apparaîtra sur tous les contrats PDF (côté plateforme).
                  </p>
                  {adminSignature && (
                    <div className="border border-border rounded-lg p-3 bg-white inline-block">
                      <img src={adminSignature} alt="Signature" className="max-h-16 object-contain" data-testid="admin-signature-preview" />
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <Input
                      type="file"
                      accept="image/*"
                      className="max-w-xs"
                      data-testid="admin-signature-upload"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = async (ev) => {
                          const base64 = ev.target.result;
                          try {
                            await axios.put(`${API}/api/settings/admin-signature`, { admin_signature: base64 }, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            setAdminSignature(base64);
                            toast.success('Signature enregistrée');
                          } catch { toast.error('Erreur'); }
                        };
                        reader.readAsDataURL(file);
                      }}
                    />
                    {adminSignature && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-500"
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/api/settings/admin-signature`, { admin_signature: null }, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            setAdminSignature(null);
                            toast.success('Signature supprimée');
                          } catch { toast.error('Erreur'); }
                        }}
                        data-testid="admin-signature-delete"
                      >
                        Supprimer
                      </Button>
                    )}
                  </div>
                  <div className="space-y-2 mt-3">
                    <Label className="text-xs">Texte sous la signature (apparaît sur le contrat)</Label>
                    <Input
                      value={adminSignatureLabel}
                      onChange={(e) => setAdminSignatureLabel(e.target.value)}
                      placeholder={`ex: Responsable commercial ${platformSettings.platform_name || 'Plateforme'}`}
                      data-testid="admin-signature-label-input"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          await axios.put(`${API}/api/settings/platform`, { admin_signature_label: adminSignatureLabel }, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          toast.success('Texte de signature enregistré');
                        } catch { toast.error('Erreur'); }
                      }}
                      data-testid="save-signature-label-btn"
                    >
                      <Save className="w-3 h-3 mr-1" />
                      Enregistrer le texte
                    </Button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button onClick={savePlatformSettings} disabled={saving} data-testid="save-platform-btn">
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Client Settings Tab */}
          <TabsContent value="clients" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Sécurité Client
                </CardTitle>
                <CardDescription>
                  Configuration de l&apos;authentification et des sessions client
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* PIN Settings */}
                <div className="p-4 rounded-lg border">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <Label className="text-base">PIN de sécurité client</Label>
                      <p className="text-sm text-muted-foreground">
                        Authentification à deux facteurs avec PIN
                      </p>
                    </div>
                    <Switch
                      checked={templateSettings.pin_enabled}
                      onCheckedChange={(checked) => setTemplateSettings(prev => ({ ...prev, pin_enabled: checked }))}
                      data-testid="pin-enabled-switch"
                    />
                  </div>
                  {templateSettings.pin_enabled && (
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div className="space-y-2">
                        <Label>Longueur du PIN</Label>
                        <select
                          className="w-full h-10 px-3 rounded-md border border-input bg-background"
                          value={templateSettings.pin_length}
                          onChange={(e) => setTemplateSettings(prev => ({ ...prev, pin_length: parseInt(e.target.value) }))}
                        >
                          <option value={4}>4 chiffres</option>
                          <option value={6}>6 chiffres</option>
                          <option value={8}>8 chiffres</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label>Login — Tentatives max avant blocage</Label>
                        <select
                          className="w-full h-10 px-3 rounded-md border border-input bg-background"
                          value={templateSettings.login_max_attempts || 0}
                          onChange={(e) => setTemplateSettings(prev => ({ ...prev, login_max_attempts: parseInt(e.target.value) }))}
                        >
                          <option value={0}>Désactivé</option>
                          <option value={3}>3 tentatives</option>
                          <option value={5}>5 tentatives</option>
                          <option value={10}>10 tentatives</option>
                        </select>
                        <p className="text-xs text-muted-foreground">Blocage 5 min après X erreurs de mot de passe (clients uniquement)</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Code PIN — Tentatives max avant blocage</Label>
                        <select
                          className="w-full h-10 px-3 rounded-md border border-input bg-background"
                          value={templateSettings.pin_max_attempts}
                          onChange={(e) => setTemplateSettings(prev => ({ ...prev, pin_max_attempts: parseInt(e.target.value) }))}
                        >
                          <option value={0}>Désactivé</option>
                          <option value={3}>3 tentatives</option>
                          <option value={5}>5 tentatives</option>
                          <option value={10}>10 tentatives</option>
                        </select>
                        <p className="text-xs text-muted-foreground">Blocage 5 min après X erreurs de code PIN d'accès</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Session Settings */}
                <div className="p-4 rounded-lg border">
                  <div className="flex items-center gap-3 mb-4">
                    <Clock className="w-5 h-5 text-blue-500" />
                    <div>
                      <Label className="text-base">Gestion des Sessions</Label>
                      <p className="text-sm text-muted-foreground">
                        Configuration de l&apos;expiration et de l&apos;avertissement de session
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Durée de session (minutes)</Label>
                      <Input
                        type="number"
                        value={templateSettings.session_timeout_minutes}
                        onChange={(e) => setTemplateSettings(prev => ({ ...prev, session_timeout_minutes: parseInt(e.target.value) }))}
                        min={5}
                        max={120}
                        data-testid="session-timeout-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        La session expire après cette durée d&apos;inactivité
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label>Avertissement avant expiration (minutes)</Label>
                      <Input
                        type="number"
                        value={templateSettings.session_warning_minutes}
                        onChange={(e) => setTemplateSettings(prev => ({ ...prev, session_warning_minutes: parseInt(e.target.value) }))}
                        min={1}
                        max={30}
                        data-testid="session-warning-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Affiche un avertissement X minutes avant l&apos;expiration
                      </p>
                    </div>
                  </div>
                </div>

                {/* Default Portfolio Settings */}
                <div className="p-4 rounded-lg border">
                  <div className="flex items-center gap-3 mb-4">
                    <Wallet className="w-5 h-5 text-emerald-500" />
                    <div>
                      <Label className="text-base">Portefeuille par défaut</Label>
                      <p className="text-sm text-muted-foreground">
                        Paramètres initiaux pour les nouveaux clients
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Devise par défaut</Label>
                      <select
                        className="w-full h-10 px-3 rounded-md border border-input bg-background"
                        value={templateSettings.default_currency}
                        onChange={(e) => setTemplateSettings(prev => ({ ...prev, default_currency: e.target.value }))}
                      >
                        <option value="EUR">EUR - Euro</option>
                        <option value="USD">USD - Dollar</option>
                        <option value="CHF">CHF - Franc Suisse</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Solde initial (€)</Label>
                      <Input
                        type="number"
                        value={templateSettings.default_portfolio_balance}
                        onChange={(e) => setTemplateSettings(prev => ({ ...prev, default_portfolio_balance: parseFloat(e.target.value) }))}
                        min={0}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button onClick={saveTemplateSettings} disabled={saving} data-testid="save-template-btn">
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Scheduler Tab */}
          <TabsContent value="scheduler" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Tâches Automatisées
                  {schedulerStatus.scheduler_running ? (
                    <Badge className="bg-emerald-500/20 text-emerald-500 ml-2">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Actif
                    </Badge>
                  ) : (
                    <Badge variant="destructive" className="ml-2">
                      <XCircle className="w-3 h-3 mr-1" />
                      Inactif
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Gestion des tâches planifiées (calcul d&apos;intérêts, vérifications)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Jobs List */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-base">Tâches planifiées</Label>
                    <Button variant="outline" size="sm" onClick={fetchSchedulerStatus}>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Rafraîchir
                    </Button>
                  </div>
                  
                  {schedulerStatus.jobs.length === 0 ? (
                    <div className="text-center py-8 bg-muted/30 rounded-lg">
                      <Clock className="w-10 h-10 mx-auto text-muted-foreground/50 mb-2" />
                      <p className="text-muted-foreground">Aucune tâche planifiée</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {schedulerStatus.jobs.map((job) => (
                        <div key={job.id} className="p-4 rounded-lg border bg-card/50 flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="p-2 rounded-full bg-primary/10">
                              {job.id === 'interest_calculation' ? (
                                <Percent className="w-5 h-5 text-primary" />
                              ) : (
                                <Calendar className="w-5 h-5 text-primary" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium">{job.name}</p>
                              <p className="text-sm text-muted-foreground">
                                Prochain: {job.next_run_time 
                                  ? new Date(job.next_run_time).toLocaleString('fr-FR')
                                  : 'Non planifié'}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {job.trigger}
                              </p>
                            </div>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => triggerJob(job.id)}
                            data-testid={`trigger-${job.id}`}
                          >
                            <Play className="w-4 h-4 mr-2" />
                            Exécuter maintenant
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Recent Logs */}
                <div className="space-y-4">
                  <Label className="text-base">Historique des exécutions</Label>
                  {schedulerStatus.recent_logs.length === 0 ? (
                    <div className="text-center py-6 bg-muted/30 rounded-lg">
                      <p className="text-muted-foreground text-sm">Aucun historique disponible</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {schedulerStatus.recent_logs.map((log) => (
                        <div 
                          key={log.id} 
                          className={`p-3 rounded-lg border flex items-center justify-between ${
                            log.status === 'success' ? 'bg-emerald-500/5 border-emerald-500/20' : 
                            log.status === 'error' ? 'bg-red-500/5 border-red-500/20' : 'bg-muted/30'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            {log.status === 'success' ? (
                              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                            ) : log.status === 'error' ? (
                              <XCircle className="w-4 h-4 text-red-500" />
                            ) : (
                              <AlertTriangle className="w-4 h-4 text-amber-500" />
                            )}
                            <div>
                              <p className="text-sm font-medium">{log.type}</p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(log.executed_at).toLocaleString('fr-FR')}
                              </p>
                            </div>
                          </div>
                          {log.result && (
                            <Badge variant="outline">
                              {log.result.total_paid?.toFixed(2) || 0}€ versés
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Configuration des Notifications
                </CardTitle>
                <CardDescription>
                  Activez ou désactivez les notifications automatiques
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Transaction Notifications */}
                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-blue-500/10">
                      <Wallet className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <Label className="text-base">Notifications de transactions</Label>
                      <p className="text-sm text-muted-foreground">
                        Notifier le client à chaque nouvelle transaction
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={notificationSettings.transaction_notifications}
                    onCheckedChange={(checked) => setNotificationSettings(prev => ({ 
                      ...prev, 
                      transaction_notifications: checked 
                    }))}
                    data-testid="transaction-notif-switch"
                  />
                </div>

                {/* Interest Notifications */}
                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-emerald-500/10">
                      <Percent className="w-5 h-5 text-emerald-500" />
                    </div>
                    <div>
                      <Label className="text-base">Notifications d&apos;intérêts</Label>
                      <p className="text-sm text-muted-foreground">
                        Notifier le client lors du versement d&apos;intérêts
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={notificationSettings.interest_notifications}
                    onCheckedChange={(checked) => setNotificationSettings(prev => ({ 
                      ...prev, 
                      interest_notifications: checked 
                    }))}
                    data-testid="interest-notif-switch"
                  />
                </div>

                {/* Contract Expiry Notifications */}
                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-amber-500/10">
                      <Calendar className="w-5 h-5 text-amber-500" />
                    </div>
                    <div>
                      <Label className="text-base">Alertes d&apos;échéance</Label>
                      <p className="text-sm text-muted-foreground">
                        Notifier le client avant l&apos;expiration de ses contrats
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={notificationSettings.contract_expiry_notifications}
                    onCheckedChange={(checked) => setNotificationSettings(prev => ({ 
                      ...prev, 
                      contract_expiry_notifications: checked 
                    }))}
                    data-testid="contract-expiry-notif-switch"
                  />
                </div>

                {/* KYC Notifications */}
                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-purple-500/10">
                      <Shield className="w-5 h-5 text-purple-500" />
                    </div>
                    <div>
                      <Label className="text-base">Notifications KYC</Label>
                      <p className="text-sm text-muted-foreground">
                        Notifier les admins lors des changements de statut KYC
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={notificationSettings.kyc_notifications}
                    onCheckedChange={(checked) => setNotificationSettings(prev => ({ 
                      ...prev, 
                      kyc_notifications: checked 
                    }))}
                    data-testid="kyc-notif-switch"
                  />
                </div>

                <div className="flex justify-end pt-4">
                  <Button onClick={saveTemplateSettings} disabled={saving} data-testid="save-notifications-btn">
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Backup Tab */}
          <TabsContent value="backup" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Sauvegarde de la base de données
                </CardTitle>
                <CardDescription>Créez, téléchargez et restaurez des sauvegardes complètes</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">

                {/* Actions */}
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={async () => {
                      setBackupLoading(true);
                      try {
                        const res = await axios.post(`${API}/api/backup/create`, {}, { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 });
                        toast.success(`Sauvegarde créée (${res.data.total_documents} documents)`);
                        fetchBackups();
                      } catch { toast.error('Erreur sauvegarde'); }
                      finally { setBackupLoading(false); }
                    }}
                    disabled={backupLoading}
                    data-testid="create-backup-btn"
                  >
                    {backupLoading ? 'Sauvegarde...' : 'Sauvegarder maintenant'}
                  </Button>

                  <label>
                    <input
                      type="file"
                      accept=".gz,.json"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        if (!window.confirm('ATTENTION : cela va remplacer TOUTES les données actuelles. Continuer ?')) return;
                        setBackupLoading(true);
                        try {
                          const formData = new FormData();
                          formData.append('file', file);
                          const res = await axios.post(`${API}/api/backup/restore`, formData, {
                            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                          });
                          toast.success(`Restauration réussie (${res.data.documents_restored} documents)`);
                          fetchBackups();
                        } catch (err) { toast.error(err.response?.data?.detail || 'Erreur restauration'); }
                        finally { setBackupLoading(false); }
                      }}
                      data-testid="restore-backup-input"
                    />
                    <Button variant="outline" asChild>
                      <span>Importer une sauvegarde</span>
                    </Button>
                  </label>
                </div>

                {/* Backup config */}
                <div className="p-4 rounded-lg border bg-muted/20 space-y-4">
                  <Label className="font-semibold">Sauvegarde automatique</Label>
                  
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Nom du sous-dossier sur le Drive (par defaut : nom de la plateforme)</Label>
                    <Input
                      value={platformSettings.backup_name || ''}
                      onChange={(e) => setPlatformSettings(p => ({...p, backup_name: e.target.value}))}
                      placeholder={platformSettings.platform_name || 'Nom de la plateforme'}
                      className="max-w-xs text-sm h-8"
                      data-testid="backup-name-input"
                    />
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground">Data (clients, transactions...) :</span>
                      <select
                        className="h-9 px-3 rounded-md border border-input bg-background text-sm"
                        value={backupInterval}
                        onChange={(e) => setBackupInterval(parseInt(e.target.value))}
                        data-testid="backup-interval-select"
                      >
                        <option value={5}>5 minutes</option>
                        <option value={10}>10 minutes</option>
                        <option value={15}>15 minutes</option>
                        <option value={30}>30 minutes</option>
                        <option value={60}>1 heure</option>
                        <option value={120}>2 heures</option>
                        <option value={360}>6 heures</option>
                        <option value={720}>12 heures</option>
                        <option value={1440}>24 heures</option>
                      </select>
                      <span className="text-xs text-muted-foreground">(20 dernières conservées)</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground">Media (logo, images, vidéo...) :</span>
                      <select
                        className="h-9 px-3 rounded-md border border-input bg-background text-sm"
                        value={platformSettings.media_backup_hours || 8}
                        onChange={(e) => setPlatformSettings(p => ({...p, media_backup_hours: parseInt(e.target.value)}))}
                        data-testid="media-backup-interval-select"
                      >
                        <option value={4}>4 heures</option>
                        <option value={8}>8 heures</option>
                        <option value={12}>12 heures</option>
                        <option value={24}>24 heures</option>
                      </select>
                      <span className="text-xs text-muted-foreground">(5 dernières conservées)</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground">Documents (uploads, fichiers) :</span>
                      <select
                        className="h-9 px-3 rounded-md border border-input bg-background text-sm"
                        value={platformSettings.documents_backup_hours || 24}
                        onChange={(e) => setPlatformSettings(p => ({...p, documents_backup_hours: parseInt(e.target.value)}))}
                        data-testid="docs-backup-interval-select"
                      >
                        <option value={8}>8 heures</option>
                        <option value={12}>12 heures</option>
                        <option value={24}>24 heures</option>
                        <option value={48}>48 heures</option>
                      </select>
                      <span className="text-xs text-muted-foreground">(5 dernières conservées)</span>
                    </div>
                  </div>

                  <Button variant="outline" size="sm" onClick={async () => {
                    try {
                      await axios.put(`${API}/api/backup/settings`, {
                        backup_name: platformSettings.backup_name,
                        backup_interval_minutes: backupInterval,
                        media_backup_hours: platformSettings.media_backup_hours || 8,
                        documents_backup_hours: platformSettings.documents_backup_hours || 24
                      }, { headers: { Authorization: `Bearer ${token}` } });
                      toast.success('Paramètres de sauvegarde enregistrés');
                    } catch { toast.error('Erreur'); }
                  }} data-testid="save-backup-settings-btn">
                    Enregistrer les paramètres
                  </Button>
                  <Button variant="outline" size="sm" onClick={async () => {
                    try {
                      const res = await axios.get(`${API}/backup/logs`, { headers: { Authorization: `Bearer ${token}` } });
                      const logs = res.data.logs || [];
                      setShowBackupLogs(logs);
                    } catch { toast.error('Erreur'); }
                  }} data-testid="view-backup-logs-btn">
                    Voir les logs
                  </Button>
                </div>

                {showBackupLogs && (
                  <div className="mt-3 p-3 rounded-lg bg-black/80 border border-border max-h-[400px] overflow-y-auto">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-green-400 font-mono">Logs sauvegarde (50 derniers)</span>
                      <button onClick={() => setShowBackupLogs(null)} className="text-xs text-muted-foreground hover:text-white">Fermer</button>
                    </div>
                    {(showBackupLogs || []).map((log, i) => (
                      <p key={i} className={`text-[10px] font-mono leading-relaxed ${log.includes('FAILED') || log.includes('ERROR') || log.includes('error') ? 'text-red-400' : log.includes('OK') ? 'text-green-400' : 'text-gray-400'}`}>{log}</p>
                    ))}
                    {showBackupLogs.length === 0 && <p className="text-xs text-muted-foreground">Aucun log trouvé</p>}
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={async () => {
                      setBackupLoading(true);
                      try {
                        const res = await axios.post(`${API}/api/backup/create`, {}, { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 });
                        toast.success(`Sauvegarde locale (${res.data.total_documents} docs)`);
                        fetchBackups();
                      } catch { toast.error('Erreur'); }
                      finally { setBackupLoading(false); }
                    }}
                    disabled={backupLoading}
                    data-testid="create-backup-btn"
                  >
                    {backupLoading ? 'Sauvegarde...' : 'Sauvegarder les données'}
                  </Button>

                  <Button
                    variant="outline"
                    onClick={async () => {
                      setBackupLoading(true);
                      try {
                        const res = await axios.post(`${API}/api/backup/create-full`, {}, { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 });
                        toast.success(`Sauvegarde complète (${res.data.total_documents} docs, ${Math.round(res.data.size_bytes / 1024)} Ko)`);
                        fetchBackups();
                      } catch { toast.error('Erreur'); }
                      finally { setBackupLoading(false); }
                    }}
                    disabled={backupLoading}
                    data-testid="create-full-backup-btn"
                  >
                    {backupLoading ? 'Sauvegarde...' : 'Sauvegarder le design (médias)'}
                  </Button>

                  <Button
                    variant="outline"
                    onClick={async () => {
                      setBackupLoading(true);
                      try {
                        const res = await axios.post(`${API}/api/backup/create-and-upload`, {}, { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 });
                        toast.success('Sauvegarde données + cloud déclenchée. Elle apparaîtra sur le Drive dans 1-2 minutes.');
                        setTimeout(fetchBackups, 60000);
                      } catch { toast.error('Erreur'); }
                      finally { setBackupLoading(false); }
                    }}
                    disabled={backupLoading}
                    data-testid="create-upload-backup-btn"
                  >
                    {backupLoading ? 'Envoi...' : 'Données + Cloud'}
                  </Button>

                  <Button
                    variant="outline"
                    onClick={async () => {
                      setBackupLoading(true);
                      try {
                        const res = await axios.post(`${API}/api/backup/create-and-upload-media`, {}, { headers: { Authorization: `Bearer ${token}` }, timeout: 300000 });
                        toast.success(res.data.drive_uploaded
                          ? `Design envoyé sur le cloud (${res.data.total_documents} items)`
                          : 'Sauvegarde média effectuée'
                        );
                        fetchBackups();
                      } catch { toast.error('Erreur'); }
                      finally { setBackupLoading(false); }
                    }}
                    disabled={backupLoading}
                    data-testid="create-upload-media-btn"
                  >
                    {backupLoading ? 'Envoi...' : 'Design + Cloud'}
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      setBackupLoading(true);
                      try {
                        const res = await axios.post(`${API}/api/backup/create-and-upload-documents`, {}, { headers: { Authorization: `Bearer ${token}` }, timeout: 300000 });
                        toast.success(res.data.drive_uploaded
                          ? `Documents envoyés sur le cloud (${res.data.total_documents} fichiers)`
                          : 'Sauvegarde documents effectuée'
                        );
                        fetchBackups();
                      } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
                      finally { setBackupLoading(false); }
                    }}
                    disabled={backupLoading}
                    data-testid="create-upload-docs-btn"
                  >
                    {backupLoading ? 'Envoi...' : 'Documents + Cloud'}
                  </Button>

                  <label>
                    <input type="file" accept=".gz,.json" className="hidden" onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      if (!window.confirm('ATTENTION : cela remplace TOUTES les données. Continuer ?')) return;
                      setBackupLoading(true);
                      try {
                        const formData = new FormData();
                        formData.append('file', file);
                        const res = await axios.post(`${API}/api/backup/restore`, formData, {
                          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                        });
                        toast.success(`Restauré (${res.data.documents_restored} docs)`);
                        fetchBackups();
                      } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
                      finally { setBackupLoading(false); }
                    }} data-testid="restore-backup-input" />
                    <Button variant="outline" asChild><span>Importer une sauvegarde</span></Button>
                  </label>
                </div>

                {/* Backup list */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="font-semibold">Sauvegardes disponibles</Label>
                    <Button variant="ghost" size="sm" onClick={fetchBackups} data-testid="refresh-backups-btn">
                      Rafraîchir
                    </Button>
                  </div>
                  {backups.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Shield className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p>Aucune sauvegarde</p>
                      <Button variant="outline" size="sm" className="mt-2" onClick={fetchBackups}>Charger</Button>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {backups.map(b => (
                        <div key={b.id} className="flex items-center justify-between p-3 rounded-lg border bg-card" data-testid={`backup-${b.id}`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-2 rounded-full ${b.status === 'media' ? 'bg-purple-500' : b.status === 'data' || b.status === 'auto' || b.status === 'light' ? 'bg-blue-500' : b.status === 'auto-full' || b.status === 'full' ? 'bg-amber-500' : 'bg-green-500'}`} />
                            <div>
                              <p className="text-sm font-medium">
                                {new Date(b.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {b.total_documents} docs — {(b.size_bytes / 1024).toFixed(0)} Ko
                                {b.status === 'data' && ' — données'}{b.status === 'media' && ' — médias/design'}{b.status === 'auto' && ' — auto données'}{b.status === 'auto-full' && ' — auto complet'}{b.status === 'full' && ' — complet'}{b.status === 'light' && ' — données'}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost" size="sm"
                              onClick={async () => {
                                try {
                                  const res = await axios.get(`${API}/api/backup/download/${b.id}`, {
                                    headers: { Authorization: `Bearer ${token}` },
                                    responseType: 'blob'
                                  });
                                  const url = URL.createObjectURL(new Blob([res.data]));
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `backup-${b.created_at.slice(0,10)}.json.gz`;
                                  a.click();
                                  URL.revokeObjectURL(url);
                                } catch { toast.error('Erreur téléchargement'); }
                              }}
                              data-testid={`download-backup-${b.id}`}
                            >
                              Télécharger
                            </Button>
                            <Button
                              variant="ghost" size="sm" className="text-red-500"
                              onClick={async () => {
                                if (!window.confirm('Supprimer cette sauvegarde ?')) return;
                                try {
                                  await axios.delete(`${API}/api/backup/${b.id}`, { headers: { Authorization: `Bearer ${token}` } });
                                  toast.success('Supprimé');
                                  fetchBackups();
                                } catch { toast.error('Erreur'); }
                              }}
                              data-testid={`delete-backup-${b.id}`}
                            >
                              Supprimer
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* External Storage */}
                <div className="border-t pt-6 space-y-4">
                  <div>
                    <Label className="font-semibold text-base">Stockage externe (optionnel)</Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Envoyez automatiquement les sauvegardes vers un service cloud. Les sauvegardes locales restent toujours disponibles.
                    </p>
                    <Button variant="link" size="sm" className="text-xs px-0 h-auto" onClick={() => setShowStorageGuide(true)} data-testid="show-storage-guide">
                      Comment configurer ?
                    </Button>
                  </div>

                  {/* Google Drive */}
                  <div className="p-4 rounded-lg border bg-muted/10 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded bg-white flex items-center justify-center">
                          <svg viewBox="0 0 87.3 78" className="w-5 h-5"><path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/><path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0-1.2 4.5h27.5z" fill="#00ac47"/><path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5H59.85z" fill="#ea4335"/><path d="M43.65 25 57.4 1.2C56.05.4 54.5 0 52.9 0H34.4c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/><path d="m59.8 53h-32.3L13.75 76.8c1.35.8 2.9 1.2 4.5 1.2h22.6c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc"/><path d="M73.4 26.5 60.65 3.3c-.8-1.4-1.95-2.5-3.3-3.3L43.6 25l16.2 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00"/></svg>
                        </div>
                        <div>
                          <p className="font-semibold text-sm">Google Drive</p>
                          <p className="text-[10px] text-muted-foreground">15 Go gratuits</p>
                        </div>
                      </div>
                      {platformSettings.gdrive_connected ? (
                        <Badge className="bg-green-500">Connecté</Badge>
                      ) : (
                        <Badge variant="outline">Non connecté</Badge>
                      )}
                    </div>

                    {platformSettings.gdrive_connected ? (
                      <div className="flex items-center justify-between p-3 rounded bg-green-500/10 border border-green-500/20">
                        <p className="text-sm text-green-400">Google Drive connecté — les sauvegardes sont envoyées automatiquement</p>
                        <Button variant="outline" size="sm" className="text-red-500" onClick={async () => {
                          if (!window.confirm('Déconnecter Google Drive ?')) return;
                          try {
                            await axios.post(`${API}/api/backup/drive/disconnect`, {}, { headers: { Authorization: `Bearer ${token}` } });
                            setPlatformSettings(p => ({...p, gdrive_connected: false}));
                            toast.success('Google Drive déconnecté');
                          } catch { toast.error('Erreur'); }
                        }} data-testid="disconnect-gdrive-btn">
                          Déconnecter
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {/* Step 1 */}
                        <div className="flex gap-3">
                          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary text-xs font-bold flex items-center justify-center">1</div>
                          <div className="flex-1 space-y-2">
                            <p className="text-xs">Entrez vos identifiants Google Cloud (<a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" className="text-primary underline">créer ici</a>)</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              <Input placeholder="Client ID" className="text-xs h-8" data-testid="gdrive-client-id"
                                value={platformSettings.gdrive_client_id || ''} onChange={(e) => setPlatformSettings(p => ({...p, gdrive_client_id: e.target.value}))} />
                              <Input placeholder="Client Secret" type="password" className="text-xs h-8" data-testid="gdrive-client-secret"
                                value={platformSettings.gdrive_client_secret || ''} onChange={(e) => setPlatformSettings(p => ({...p, gdrive_client_secret: e.target.value}))} />
                            </div>
                            <Button variant="outline" size="sm" onClick={async () => {
                              try {
                                await axios.put(`${API}/api/backup/settings`, {
                                  gdrive_client_id: platformSettings.gdrive_client_id,
                                  gdrive_client_secret: platformSettings.gdrive_client_secret
                                }, { headers: { Authorization: `Bearer ${token}` } });
                                toast.success('Identifiants enregistrés — passez à l\'étape 2');
                              } catch { toast.error('Erreur'); }
                            }} data-testid="save-gdrive-creds">
                              Enregistrer les identifiants
                            </Button>
                          </div>
                        </div>

                        {/* Step 2 */}
                        <div className="flex gap-3">
                          <div className={`flex-shrink-0 w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center ${platformSettings.gdrive_client_id ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'}`}>2</div>
                          <div className="flex-1">
                            <p className="text-xs text-muted-foreground mb-2">Connectez votre compte Google</p>
                            <Button size="sm" disabled={!platformSettings.gdrive_client_id} onClick={async () => {
                              try {
                                const res = await axios.get(`${API}/api/backup/drive/connect`, { headers: { Authorization: `Bearer ${token}` } });
                                if (res.data.authorization_url) {
                                  window.location.href = res.data.authorization_url;
                                }
                              } catch (err) { toast.error(err.response?.data?.detail || 'Erreur — vérifiez vos identifiants'); }
                            }} data-testid="connect-gdrive-btn">
                              Connecter Google Drive
                            </Button>
                          </div>
                        </div>

                        <p className="text-[10px] text-muted-foreground">
                          URI de redirection à configurer dans Google Cloud : <code className="bg-muted px-1 rounded text-[9px]">{typeof window !== 'undefined' ? window.location.origin : ''}/api/backup/drive/callback</code>
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Backblaze B2 */}
                  <div className="p-4 rounded-lg border bg-muted/10 space-y-3">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded bg-red-600 flex items-center justify-center text-white text-xs font-bold">B2</div>
                      <div>
                        <p className="font-semibold text-sm">Backblaze B2</p>
                        <p className="text-[10px] text-muted-foreground">10 Go gratuits — backblaze.com</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <Input placeholder="Key ID" className="text-xs h-8" data-testid="b2-key-id"
                        value={platformSettings.b2_key_id || ''} onChange={(e) => setPlatformSettings(p => ({...p, b2_key_id: e.target.value}))} />
                      <Input placeholder="Application Key" type="password" className="text-xs h-8" data-testid="b2-app-key"
                        value={platformSettings.b2_app_key || ''} onChange={(e) => setPlatformSettings(p => ({...p, b2_app_key: e.target.value}))} />
                      <Input placeholder="Nom du bucket" className="text-xs h-8" data-testid="b2-bucket"
                        value={platformSettings.b2_bucket || ''} onChange={(e) => setPlatformSettings(p => ({...p, b2_bucket: e.target.value}))} />
                    </div>
                    <Button variant="outline" size="sm" onClick={async () => {
                      try {
                        await axios.put(`${API}/api/backup/settings`, {
                          b2_key_id: platformSettings.b2_key_id, b2_app_key: platformSettings.b2_app_key, b2_bucket: platformSettings.b2_bucket
                        }, { headers: { Authorization: `Bearer ${token}` } });
                        toast.success('Configuration Backblaze B2 enregistrée');
                      } catch { toast.error('Erreur'); }
                    }} data-testid="save-b2-config">
                      Enregistrer B2
                    </Button>
                  </div>

                  {/* Cloudflare R2 */}
                  <div className="p-4 rounded-lg border bg-muted/10 space-y-3">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded bg-orange-500 flex items-center justify-center text-white text-xs font-bold">R2</div>
                      <div>
                        <p className="font-semibold text-sm">Cloudflare R2</p>
                        <p className="text-[10px] text-muted-foreground">10 Go gratuits — dash.cloudflare.com</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <Input placeholder="Account ID" className="text-xs h-8" data-testid="r2-account-id"
                        value={platformSettings.r2_account_id || ''} onChange={(e) => setPlatformSettings(p => ({...p, r2_account_id: e.target.value}))} />
                      <Input placeholder="Access Key" type="password" className="text-xs h-8" data-testid="r2-access-key"
                        value={platformSettings.r2_access_key || ''} onChange={(e) => setPlatformSettings(p => ({...p, r2_access_key: e.target.value}))} />
                      <Input placeholder="Nom du bucket" className="text-xs h-8" data-testid="r2-bucket"
                        value={platformSettings.r2_bucket || ''} onChange={(e) => setPlatformSettings(p => ({...p, r2_bucket: e.target.value}))} />
                    </div>
                    <Button variant="outline" size="sm" onClick={async () => {
                      try {
                        await axios.put(`${API}/api/backup/settings`, {
                          r2_account_id: platformSettings.r2_account_id, r2_access_key: platformSettings.r2_access_key, r2_bucket: platformSettings.r2_bucket
                        }, { headers: { Authorization: `Bearer ${token}` } });
                        toast.success('Configuration Cloudflare R2 enregistrée');
                      } catch { toast.error('Erreur'); }
                    }} data-testid="save-r2-config">
                      Enregistrer R2
                    </Button>
                  </div>
                </div>

              </CardContent>
            </Card>
          </TabsContent>

          {/* Account Tab - Change Password */}
          <TabsContent value="account" className="space-y-6 mt-6">
            {/* Modifier email */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Modifier mon email de connexion
                </CardTitle>
                <CardDescription>
                  Email actuel : <strong>{user?.email}</strong>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ChangeEmailForm token={token} user={user} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lock className="w-5 h-5" />
                  Modifier mon mot de passe
                </CardTitle>
                <CardDescription>
                  Changez le mot de passe de votre compte Super Admin
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ChangePasswordForm token={token} />
              </CardContent>
            </Card>

            {/* Reset Database */}
            <Card className="border-red-500/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-500">
                  <AlertTriangle className="w-5 h-5" />
                  Réinitialiser la base de données
                </CardTitle>
                <CardDescription>
                  Supprime TOUTES les données : clients, transactions, bureaux, design, paramètres. Cette action est irréversible.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResetDatabaseButton token={token} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Monitoring Tab */}
          <TabsContent value="monitoring" className="space-y-6 mt-6">
            <ErrorMonitoringPanel token={token} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Storage Guide Modal */}
      <Dialog open={showStorageGuide} onOpenChange={setShowStorageGuide}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="storage-guide-modal">
          <DialogHeader>
            <DialogTitle>Guide — Configurer le stockage externe</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4 text-sm">

            <div className="p-4 rounded-lg border bg-blue-500/5 border-blue-500/20">
              <h3 className="font-bold text-blue-400 mb-3">GOOGLE DRIVE — 15 Go gratuits</h3>
              <div className="space-y-2 text-muted-foreground">
                <p><b className="text-foreground">Prérequis</b> : un compte Google (Gmail)</p>
                <ol className="list-decimal ml-4 space-y-2">
                  <li>Allez sur <a href="https://console.cloud.google.com" target="_blank" rel="noreferrer" className="text-primary underline">console.cloud.google.com</a> &gt; créez un <b>nouveau projet</b></li>
                  <li>Menu &gt; <b>API et services</b> &gt; <b>Bibliothèque</b> &gt; cherchez &quot;Google Drive API&quot; &gt; <b>Activer</b></li>
                  <li>Menu &gt; <b>API et services</b> &gt; <b>Écran de consentement OAuth</b> &gt; Type <b>Externe</b> &gt; remplissez le nom + email &gt; <b>Enregistrer</b></li>
                  <li className="text-amber-400 font-semibold">IMPORTANT — Cliquez <b>&quot;Publier l&apos;application&quot;</b> sur l&apos;écran de consentement OAuth (onglet Audience). Sans ça, le token expire après 7 jours et les sauvegardes s&apos;arrêtent !</li>
                  <li>Menu &gt; <b>API et services</b> &gt; <b>Identifiants</b> &gt; <b>Créer des identifiants</b> &gt; <b>ID client OAuth</b> &gt; Type <b>Application Web</b></li>
                  <li>Dans <b>URI de redirection autorisés</b>, ajoutez : <code className="text-[10px] bg-muted px-1 rounded">{typeof window !== 'undefined' ? window.location.origin : 'https://votre-site.com'}/api/backup/drive/callback</code></li>
                  <li>Copiez le <b>Client ID</b> et <b>Client Secret</b></li>
                  <li>Collez-les ci-dessous &gt; Cliquez <b>&quot;Connecter Google Drive&quot;</b></li>
                </ol>
                <div className="mt-3 p-3 rounded bg-amber-500/10 border border-amber-500/20">
                  <p className="text-xs text-amber-400 font-semibold">Si Google affiche &quot;Application non vérifiée&quot; :</p>
                  <p className="text-xs text-amber-300/80">Cliquez &quot;Paramètres avancés&quot; &gt; &quot;Accéder à [nom] (non sécurisé)&quot;. C&apos;est normal et sans danger car c&apos;est VOTRE application.</p>
                </div>
                <div className="mt-2 p-3 rounded bg-red-500/10 border border-red-500/20">
                  <p className="text-xs text-red-400 font-semibold">Si les sauvegardes s&apos;arrêtent après quelques jours :</p>
                  <p className="text-xs text-red-300/80">L&apos;application est en mode &quot;Test&quot;. Allez sur Google Cloud Console &gt; Écran de consentement OAuth &gt; cliquez &quot;Publier l&apos;application&quot; &gt; Confirmez. Puis reconnectez Google Drive ici.</p>
                </div>
                <p className="text-xs text-emerald-400 mt-2">Les backups vont dans un dossier &quot;Platform Backups&quot; sur votre Drive. Les 30 dernières sauvegardes sont conservées.</p>
              </div>
            </div>

            <div className="p-4 rounded-lg border bg-red-500/5 border-red-500/20">
              <h3 className="font-bold text-red-400 mb-3">BACKBLAZE B2 — 10 Go gratuits</h3>
              <div className="space-y-2 text-muted-foreground">
                <p><b className="text-foreground">Idéal pour</b> : stockage économique et fiable</p>
                <ol className="list-decimal ml-4 space-y-2">
                  <li>Créez un compte sur <a href="https://www.backblaze.com/sign-up/cloud-storage" target="_blank" rel="noreferrer" className="text-primary underline">backblaze.com</a></li>
                  <li><b>Buckets</b> &gt; <b>Create a Bucket</b> &gt; nommez-le &gt; <b>Private</b></li>
                  <li><b>App Keys</b> &gt; <b>Add a New Application Key</b> &gt; sélectionnez le bucket</li>
                  <li>Copiez le <b>keyID</b> et l&apos;<b>applicationKey</b> (affiché une seule fois !)</li>
                </ol>
                <p className="text-xs text-amber-400 mt-2">L&apos;Application Key n&apos;est affichée qu&apos;une seule fois. Notez-la bien.</p>
              </div>
            </div>

            <div className="p-4 rounded-lg border bg-orange-500/5 border-orange-500/20">
              <h3 className="font-bold text-orange-400 mb-3">CLOUDFLARE R2 — 10 Go gratuits</h3>
              <div className="space-y-2 text-muted-foreground">
                <p><b className="text-foreground">Idéal pour</b> : si vous utilisez déjà Cloudflare</p>
                <ol className="list-decimal ml-4 space-y-2">
                  <li>Connectez-vous sur <a href="https://dash.cloudflare.com" target="_blank" rel="noreferrer" className="text-primary underline">dash.cloudflare.com</a></li>
                  <li><b>R2 Object Storage</b> &gt; <b>Create bucket</b></li>
                  <li><b>Manage R2 API Tokens</b> &gt; <b>Create API Token</b> &gt; Object Read &amp; Write</li>
                  <li>Copiez <b>Access Key ID</b> + <b>Secret Access Key</b> + <b>Account ID</b> (dans l&apos;URL)</li>
                </ol>
              </div>
            </div>

            <div className="p-4 rounded-lg border bg-muted/30">
              <h3 className="font-bold mb-3">COMPARATIF</h3>
              <table className="w-full text-xs">
                <thead><tr className="border-b text-muted-foreground"><th className="text-left py-2"></th><th className="text-center py-2">Google Drive</th><th className="text-center py-2">Backblaze B2</th><th className="text-center py-2">Cloudflare R2</th></tr></thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-b border-border/20"><td className="py-2 font-medium text-foreground">Gratuit</td><td className="text-center">15 Go</td><td className="text-center">10 Go</td><td className="text-center">10 Go</td></tr>
                  <tr className="border-b border-border/20"><td className="py-2 font-medium text-foreground">Facilité</td><td className="text-center">Moyenne</td><td className="text-center">Simple</td><td className="text-center">Simple</td></tr>
                  <tr className="border-b border-border/20"><td className="py-2 font-medium text-foreground">Config</td><td className="text-center">OAuth2</td><td className="text-center">Clé API</td><td className="text-center">Clé API</td></tr>
                  <tr><td className="py-2 font-medium text-foreground">Accès</td><td className="text-center">Via Google Drive</td><td className="text-center">Dashboard B2</td><td className="text-center">Dashboard CF</td></tr>
                </tbody>
              </table>
            </div>

          </div>
          <DialogFooter>
            <Button onClick={() => setShowStorageGuide(false)}>Compris</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
